/* LayoutBlocks — Spacer, Divider, Heading utilities */

// ── Spacer ────────────────────────────────────────────

interface SpacerProps {
  data: Record<string, string>
}

export function SpacerBlock({ data }: SpacerProps) {
  const height = parseInt(data.height || '48', 10)

  return (
    <div
      data-testid="card-spacer-block"
      className="group relative flex items-center justify-center cursor-row-resize"
      style={{ height: `${height}px` }}
    >
      {/* Height indicator */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2">
        <div className="h-px w-8 bg-white/10" />
        <span className="text-[9px] font-mono text-white/15 bg-[#0c0c18] px-2 py-0.5 rounded-full border border-white/[0.04]">
          {height}px
        </span>
        <div className="h-px w-8 bg-white/10" />
      </div>

      {/* Side indicators */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-3 h-3 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </div>
      <div className="absolute left-1/2 bottom-0 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-3 h-3 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  )
}

// ── Divider ───────────────────────────────────────────

interface DividerProps {
  data: Record<string, string>
}

export function DividerBlock({ data }: DividerProps) {
  const style = data.style || 'gradient' // solid | gradient | dots

  return (
    <div data-testid="card-divider-block" className="py-4 flex items-center justify-center">
      {style === 'gradient' && (
        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
      )}
      {style === 'solid' && (
        <div className="w-full h-px bg-white/[0.06]" />
      )}
      {style === 'dots' && (
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-white/10" />
          <div className="w-1 h-1 rounded-full bg-white/15" />
          <div className="w-1 h-1 rounded-full bg-white/10" />
        </div>
      )}
    </div>
  )
}

// ── Heading ───────────────────────────────────────────

import InlineTextEditor from '@/components/cms/InlineTextEditor'

interface HeadingProps {
  data: Record<string, string>
  onUpdateData?: (updates: Record<string, string>) => void
}

export function HeadingBlock({ data, onUpdateData }: HeadingProps) {
  const text = data.text || 'Section Heading'
  const level = data.level || 'h2'
  const align = data.align || 'left'

  const sizeClass =
    level === 'h1' ? 'text-3xl font-bold' :
    level === 'h2' ? 'text-xl font-semibold' :
    'text-base font-medium'

  return (
    <div data-testid="card-heading-block" className={`py-2 text-${align}`}>
      {onUpdateData ? (
        <InlineTextEditor
          value={text}
          onChange={(v) => onUpdateData({ text: v })}
          variant="heading"
          placeholder="Section Heading"
          className={`${sizeClass} text-white/80 tracking-tight leading-snug`}
        />
      ) : (
        <div className={`${sizeClass} text-white/80 tracking-tight leading-snug`}>
          {text}
        </div>
      )}
      {level === 'h2' && (
        <div className="mt-2">
          <div className="w-10 h-0.5 rounded-full bg-gradient-to-r from-blue-500/40 to-transparent" style={{ marginLeft: align === 'center' ? 'auto' : '0', marginRight: align === 'center' ? 'auto' : undefined }} />
        </div>
      )}
    </div>
  )
}
